import { buttonStyle } from 'components/buttons/buttonStyle';
import { RotateCcwIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import VerticalDivider from 'components/divider/VerticalDivider';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { NewDesignIsland } from 'components/ui/overlay-motion';
import { Tooltip } from 'components/ui/tooltip';
import { useIsMobile } from 'hooks/useIsMobile';
import React, { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import QuickFilterChips from '../filter-bar/QuickFilterChips';
import {
    FilterCheckboxInForm,
    MainFilterForm,
    PeriodQuickFilter,
    QuickFilterForm,
    SelectOption,
} from '../ListTableTypes';
import TotalsToggle from './TotalsToggle';
import QuickFilterDropdown from './inputs/QuickFilterDropdown';
import {
    isDropdownMultiselect,
    isGroupDisabled,
    QuickFilterChangeHandler,
    toggleQuickFilterOption,
    useClearDisabledQuickFilters,
} from './quickFilterToggle';

export type Props = {
    quickFilters?: QuickFilterForm;
    handleChange: QuickFilterChangeHandler;
    handleDropdownChange?: (selected: SelectOption[], selector: string, comperator?: string) => void;
    clearFilters: () => void;
    hasFilters: boolean;
    mainFilterState: MainFilterForm;
    handleMainFilterStateChange: (state: MainFilterForm) => void;
    /** Optional content rendered inside the same flex slot as the quick-filter inputs, before
     *  them. Used to surface a passive legend/badge in the same row position the checkboxes
     *  would have occupied (e.g. My Portfolio's "My clients" legend). */
    leading?: React.ReactNode;
    /** When true, the divider that normally separates `leading` from the entity count to its
     *  left is suppressed — use when the caller has hidden that count, so the divider would
     *  have nothing to separate from. */
    hideLeadingDivider?: boolean;
    /** Optional content rendered as the last cell of the quick-filter row, after the Clear
     *  button. Used for a per-page view toggle that should sit at the row's right edge rather
     *  than next to the search bar. */
    trailing?: React.ReactNode;
    /** Action buttons for the list's rows (e.g. bulk actions on the selected ones), drawn after the Clear
     *  button in the same right-aligned group, so they wrap with the other controls and never take a
     *  column of their own beside the filter groups. */
    actions?: React.ReactNode;
    totalsToggleProps?: {
        enabled: boolean;
        onChange: (enabled: boolean) => void;
        disabled?: boolean;
    };
    hideClearFilters?: boolean;
    /** A Reset before Clear that sets the list back to `period`'s default preset; left out while nothing differs. */
    reset?: { onReset: () => void; period: PeriodQuickFilter };
    /** Draws the quick filters as the new design's chips, in place of the checkboxes and dropdowns. */
    chips?: boolean;
    /** The chips' period chip and what applies it; see `QuickFilterChips`. */
    period?: PeriodQuickFilter;
    applyMainFilters?: (state: MainFilterForm) => void;
};

export const OptionalTooltip = ({
    children,
    tooltip,
    tooltipValues,
}: {
    children: JSX.Element;
    tooltip?: string;
    /** Interpolated into `tooltip` — for a tooltip whose wording depends on runtime data. */
    tooltipValues?: ComponentProps<typeof InfoTooltip>['values'];
}) => {
    if (tooltip)
        return (
            <span className="inline-flex items-center">
                {children}
                <InfoTooltip text={tooltip} values={tooltipValues} />
            </span>
        );
    return children;
};

const QuickFilters: React.FC<Props> = ({
    quickFilters,
    handleChange,
    handleDropdownChange,
    clearFilters,
    hasFilters,
    mainFilterState,
    handleMainFilterStateChange,
    leading,
    hideLeadingDivider = false,
    trailing,
    actions,
    totalsToggleProps,
    hideClearFilters = false,
    reset,
    chips = false,
    period,
    applyMainFilters,
}) => {
    const matchDownSM = useIsMobile();

    // The chips clear a disabled group themselves.
    useClearDisabledQuickFilters(chips ? undefined : quickFilters, mainFilterState, handleChange);

    return (
        // One wrapping flex row: the filter groups and the right-side controls are all its items
        // (the groups' wrapper is `contents`), so the controls follow the last group on its line,
        // right-aligned via ml-auto, and take a line of their own only when that line is full.
        // A wrapper of their own would size to all groups on one line and push the controls,
        // even a collapsed Clear, onto an empty line whenever the groups need two.
        <div className="flex w-full flex-wrap items-center gap-2">
            <div className="contents">
                {leading ? (
                    // Mirror the per-filter-group divider from the iteration below so a leading
                    // legend stays visually separated from the entity count to its left, the
                    // same way a real first quick-filter group would. Skip the divider when the
                    // caller hides the count — there's nothing to separate from.
                    <>
                        {hideLeadingDivider ? null : <VerticalDivider />}
                        {leading}
                    </>
                ) : null}
                {chips && quickFilters && (Object.keys(quickFilters).length || period) ? (
                    <>
                        <VerticalDivider />
                        <NewDesignIsland className="contents">
                            <QuickFilterChips
                                quickFilters={quickFilters}
                                handleChange={handleChange}
                                handleDropdownChange={handleDropdownChange}
                                mainFilterState={mainFilterState}
                                handleMainFilterStateChange={handleMainFilterStateChange}
                                period={period}
                                applyMainFilters={applyMainFilters}
                            />
                        </NewDesignIsland>
                    </>
                ) : null}
                {quickFilters && !chips
                    ? Object.values(quickFilters).map((filter) => {
                          // Handle dropdown-multiselect type
                          if (isDropdownMultiselect(filter)) {
                              return (
                                  <React.Fragment key={`qfd-${filter.selector}`}>
                                      <VerticalDivider />
                                      <QuickFilterDropdown
                                          filter={filter}
                                          onChange={handleDropdownChange || (() => {})}
                                      />
                                  </React.Fragment>
                              );
                          }

                          // Handle checkbox group type (existing behavior)
                          const group = filter;
                          const groupDisabled = isGroupDisabled(group, mainFilterState);
                          return group?.filters?.length ? (
                              <div
                                  key={`fg-${group.selector}-${group.comperator ?? ''}`}
                                  role="group"
                                  aria-label={group.label ? t(group.label) : undefined}
                                  className="flex items-center"
                              >
                                  <VerticalDivider />
                                  {group?.label && !matchDownSM ? (
                                      <span className="pr-1">{t(group?.label)}:</span>
                                  ) : null}
                                  <div>
                                      {group.filters.map((checkboxFilter: FilterCheckboxInForm) => {
                                          return (
                                              <OptionalTooltip
                                                  tooltip={checkboxFilter?.tooltip}
                                                  tooltipValues={checkboxFilter?.tooltipValues}
                                                  key={`fg-f-${checkboxFilter.argument}`}
                                              >
                                                  {/* A 32px row: the checkbox 8px in, 8px from its label and 4px after it. */}
                                                  <label
                                                      className={cn(
                                                          'mr-1 inline-flex h-8 items-center gap-2 pl-2 whitespace-nowrap select-none',
                                                          groupDisabled
                                                              ? 'text-fg-disabled cursor-not-allowed'
                                                              : 'cursor-pointer'
                                                      )}
                                                  >
                                                      <Checkbox
                                                          variant="secondary"
                                                          checked={checkboxFilter.isSelected}
                                                          disabled={groupDisabled}
                                                          onCheckedChange={(checked) =>
                                                              toggleQuickFilterOption({
                                                                  group,
                                                                  option: checkboxFilter,
                                                                  checked,
                                                                  handleChange,
                                                                  mainFilterState,
                                                                  handleMainFilterStateChange,
                                                              })
                                                          }
                                                      />
                                                      {checkboxFilter.label}
                                                  </label>
                                              </OptionalTooltip>
                                          );
                                      })}
                                  </div>
                              </div>
                          ) : null;
                      })
                    : null}
            </div>
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                {totalsToggleProps ? (
                    <TotalsToggle
                        enabled={totalsToggleProps.enabled}
                        onChange={totalsToggleProps.onChange}
                        disabled={totalsToggleProps.disabled}
                    />
                ) : null}
                {reset ? (
                    <Tooltip
                        content={t('reset-filters-tooltip', {
                            preset:
                                reset.period.presets.find((each) => each.value === reset.period.defaultPreset)?.label ??
                                '',
                        })}
                        side="bottom"
                    >
                        <Button {...buttonStyle({ variant: 'outlined' }, 'h-9 px-4')} onClick={reset.onReset}>
                            <RotateCcwIcon className="size-4" />
                            <FormattedMessage id="reset-filters" />
                        </Button>
                    </Tooltip>
                ) : null}
                {!hideClearFilters ? (
                    // Stays mounted so the appear/disappear can run as an interruptible CSS
                    // transition (a conditional mount would just pop in and shift the row).
                    // Hidden, it collapses to 0px (border-0 and no shadow too: the kit border
                    // alone keeps 2px) and the flex gap before it stays as the toggles' right
                    // margin. max-w-40 must exceed the button's natural width so the expand
                    // reaches it.
                    <Button
                        {...buttonStyle(
                            { variant: 'outlined' },
                            cn(
                                'h-9 overflow-hidden px-4 whitespace-nowrap transition-[max-width,opacity,margin,padding]',
                                hasFilters
                                    ? 'max-w-40 opacity-100 duration-300 ease-out'
                                    : 'pointer-events-none max-w-0 border-0 px-0 opacity-0 shadow-none duration-200 ease-in'
                            )
                        )}
                        onClick={clearFilters}
                        tabIndex={hasFilters ? undefined : -1}
                        aria-hidden={!hasFilters}
                    >
                        <FormattedMessage id="clear" />
                    </Button>
                ) : null}
                {actions}
                {trailing}
            </div>
        </div>
    );
};

export default QuickFilters;
